/**
 * @fileoverview
 * Here we are making runtime-styling through material theme aware of our own theme.
 * So that stuff rendered by it matches our own styling.
 */

import { createTheme } from '@mui/material/styles';

import { muiTable, muiTableCell, muiTableRow } from './mui-components-overrides';

import {
  bodyFontFamily,
  breakpoints,
  customSizes,
  fontSize,
  fontSizes,
  fontWeights,
  headerFontFamily,
  lineHeights,
  primaryColor,
  radii,
  secondaryColor,
  textColor,
} from './theme';

const headerFontFamilyList = headerFontFamily.join(', ');

type THeaderTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const headers: Partial<Record<THeaderTag, any>> = {};

(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as THeaderTag[]).forEach((headerType) => {
  headers[headerType] = {
    fontFamily: headerFontFamilyList,
    fontWeight: fontWeights.heading,
    lineHeight: lineHeights.heading,
    my: customSizes.md,
    color: primaryColor,
  };
});

// might need to export header customization too

const typography = {
  fontFamily: bodyFontFamily.join(', '),
  fontSize,
  ...headers,
  button: {
    fontWeight: fontWeights.semibold,
    fontSize: fontSizes[3] * fontSize, // px
  },
};

type BreakpointKey = keyof typeof breakpoints;

/** Ascending, as authored in `theme.ts`. */
const breakpointKeys = Object.keys(breakpoints) as BreakpointKey[];

const toPx = (key: BreakpointKey | number): number =>
  typeof key === 'number' ? key : breakpoints[key];

/**
 * Media Queries Level 4 range syntax, matching the SCSS bands in
 * `src/styles/variables.scss`. `width < X` and `width >= X` are exact complements,
 * so a CSS band and a `useMediaQuery` band can never both match at X — MUI's own
 * `max-width: X - 0.05px` convention is correct but leaves a dead zone across the
 * fractional widths.
 *
 * All five are overridden together on purpose: `between`, `only` and `not` are built
 * inside `createBreakpoints` from its *internal* `up`/`down` closures, so replacing
 * only `up`/`down` would silently leave them on the old inclusive syntax.
 *
 * `createBreakpoints` spreads whatever else you pass over its generated functions.
 */
const rangeSyntaxBreakpoints = {
  up: (key: BreakpointKey | number): string => `@media (width >= ${toPx(key)}px)`,

  down: (key: BreakpointKey | number): string => `@media (width < ${toPx(key)}px)`,

  between: (start: BreakpointKey | number, end: BreakpointKey | number): string =>
    `@media (width >= ${toPx(start)}px) and (width < ${toPx(end)}px)`,

  only: (key: BreakpointKey): string => {
    const index = breakpointKeys.indexOf(key);

    if (index >= 0 && index < breakpointKeys.length - 1) {
      return rangeSyntaxBreakpoints.between(key, breakpointKeys[index + 1]);
    }

    return rangeSyntaxBreakpoints.up(key);
  },

  not: (key: BreakpointKey): string => {
    const index = breakpointKeys.indexOf(key);

    if (index === 0) {
      return rangeSyntaxBreakpoints.up(breakpointKeys[1]);
    }

    if (index === breakpointKeys.length - 1) {
      return rangeSyntaxBreakpoints.down(key);
    }

    return rangeSyntaxBreakpoints
      .between(key, breakpointKeys[index + 1])
      .replace('@media', '@media not all and');
  },
};

export const theme = createTheme({
  palette: {
    primary: {
      main: primaryColor,
    },
    secondary: {
      main: secondaryColor,
    },
    text: {
      primary: textColor,
      secondary: textColor,
    },
  },
  typography,
  breakpoints: {
    values: breakpoints,
    ...rangeSyntaxBreakpoints,
  },
  shape: {
    borderRadius: radii.md, // radii.md
  },
  components: {
    // @ts-expect-error TS can't pick up enums from strings
    MuiTable: muiTable,
    MuiTableCell: muiTableCell,
    MuiTableRow: muiTableRow,
  },
});
